import React, { FC } from 'react';
import clsx from 'clsx';
import { IDerivedMintInfoWithProvider } from '@app/state/mintStore';
import { SubmitButton } from '@app/components/SubmitButton';
import { Web3Button } from '@thirdweb-dev/react';
import { showErrorToast, showSuccessToast } from '@app/components/Toast';
import { Alert, AlertDescription, AlertIcon } from '@chakra-ui/alert';
import { useConfig } from '@app/config';
import { useLiquidityWizardState } from '@app/screens/liquidity/LiquidityWizard/state';

import s from '@app/screens/airdrop/ClaimsList/ClaimsList.module.scss';
import { AddLiquidityWithProviderButtonETH } from '@app/screens/liquidity/LiquidityWizard/components/SelectModeStep/components/AddLiquidityWithProviderButtonETH';
import AddLiquidityWithProviderButton from '@app/screens/liquidity/LiquidityWizard/components/SelectModeStep/components/AddLiquidityWithProviderButton';

interface Props {
  mintInfo: IDerivedMintInfoWithProvider;
}

export const CreatePositionWithKpNft: FC<Props> = ({ mintInfo }) => {
  const {
    currencyA,
    currencyB,
    actions: { setStep }
  } = useLiquidityWizardState();
  const config = useConfig();

  if (!mintInfo.token0 || !mintInfo.token1) {
    return null;
  }

  const isReady =
    mintInfo.parsedAmounts.CURRENCY_A?.greaterThan(0) &&
    mintInfo.parsedAmounts.CURRENCY_B?.greaterThan(0);

  if (!isReady || mintInfo.errorMessage) {
    return (
      <SubmitButton
        isLoading={false}
        label={mintInfo.errorMessage ?? 'Create position'}
        disabled
      />
    );
  }

  return (
    <>
      {mintInfo.nftPoolAddress === null ? (
        <>
          <Alert
            status="warning"
            display="flex"
            border="1px solid"
            borderColor="neutral.700"
            my="16px"
            borderRadius="10px"
            py="10px"
            px="12px"
          >
            <AlertIcon w="16px" h="16px" mr="8px" color="neutral.400" />
            <AlertDescription color="neutral.400">
              You are the very first kpNFT minter for this asset! You will need
              to initialize kpNFT contract first.
            </AlertDescription>
          </Alert>

          <Web3Button
            className={clsx(s.claimBtn, s.outlined)}
            style={{ width: '100%' }}
            contractAddress={config?.CONTRACTS.NFT_POOL_FACTORY!}
            action={async contract =>
              contract.call('createPool', [mintInfo.vaultAddress])
            }
            onError={error => {
              console.error(error);
              showErrorToast('Failed to initialize pool');
            }}
            onSuccess={async () => {
              showSuccessToast('Successfully initialized NFT pool');
              await mintInfo.getPoolAddress();
            }}
            isDisabled={false}
          >
            Initialize NFT pool
          </Web3Button>
        </>
      ) : (
        <>
          {currencyA?.isNative || currencyB?.isNative ? (
            <AddLiquidityWithProviderButtonETH
              baseCurrency={currencyA}
              quoteCurrency={currencyB}
              mintInfo={mintInfo}
              poolAddress={mintInfo.vaultAddress}
              onComplete={() => {
                setStep(2);
              }}
            />
          ) : (
            <AddLiquidityWithProviderButton
              baseCurrency={currencyA}
              quoteCurrency={currencyB}
              mintInfo={mintInfo}
              poolAddress={mintInfo.vaultAddress}
              onComplete={() => {
                setStep(2);
              }}
            />
          )}
        </>
      )}
    </>
  );
};
